import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField,
} from "@mui/material";
import type { InvoicingSettings, SchedulingSettings } from "./types";

const VAT_KEYS = ["AAM", "TAM", "0", "5", "18", "27"];
const PAYMENT_METHODS = ["átutalás", "készpénz", "bankkártya"];

const INVOICING_DEFAULTS: InvoicingSettings = {
  item_name: "Pszichoterápiás ülés",
  unit_price_huf: 0,
  vat_key: "AAM",
  payment_method: "átutalás",
  due_days: 8,
  e_invoice: true,
  agent_key_set: false,
};

interface Props {
  settings: SchedulingSettings;
  invoicing: InvoicingSettings | null;
  onClose: () => void;
  onSave: (s: SchedulingSettings,
           inv: Partial<InvoicingSettings> & { agent_key?: string }) => void;
}

export default function SettingsDialog({ settings, invoicing, onClose, onSave }: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState<SchedulingSettings>(settings);
  const [inv, setInv] = useState<InvoicingSettings>(invoicing ?? INVOICING_DEFAULTS);
  // Blank = keep the stored key; anything typed replaces it. Never displayed.
  const [agentKey, setAgentKey] = useState("");

  const set = <K extends keyof SchedulingSettings>(k: K, v: SchedulingSettings[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  const setI = <K extends keyof InvoicingSettings>(k: K, v: InvoicingSettings[K]) =>
    setInv((f) => ({ ...f, [k]: v }));

  const save = () => {
    const { agent_key_set, ...fields } = inv;
    onSave(form, agentKey.trim() ? { ...fields, agent_key: agentKey.trim() } : fields);
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t("calendar.settings.title")}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <TextField size="small" type="time" label={t("calendar.settings.workStart")}
          value={form.work_start} onChange={(e) => set("work_start", e.target.value)}
          InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="time" label={t("calendar.settings.workEnd")}
          value={form.work_end} onChange={(e) => set("work_end", e.target.value)}
          InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="number" label={t("calendar.settings.defaultDuration")}
          value={form.default_duration_min}
          onChange={(e) => set("default_duration_min", Number(e.target.value))}
          inputProps={{ min: 5, max: 480, step: 5 }} />
        <TextField size="small" type="number" label={t("calendar.settings.defaultReminder")}
          value={form.default_reminder_lead_min}
          onChange={(e) => set("default_reminder_lead_min", Number(e.target.value))}
          inputProps={{ min: 0, step: 60 }} />
        <TextField size="small" label={t("calendar.settings.timezone")}
          value={form.timezone} onChange={(e) => set("timezone", e.target.value)} />

        <span className="cal-settings-kicker">{t("calendar.settings.invoicingTitle")}</span>
        <TextField size="small" type="password" label={t("calendar.settings.agentKey")}
          value={agentKey} onChange={(e) => setAgentKey(e.target.value)}
          placeholder={inv.agent_key_set ? t("calendar.settings.agentKeySet") : ""}
          InputLabelProps={{ shrink: true }}
          inputProps={{ autoComplete: "new-password", "data-testid": "cal-settings-agent-key" }} />
        <TextField size="small" label={t("calendar.settings.itemName")}
          value={inv.item_name} onChange={(e) => setI("item_name", e.target.value)} />
        <TextField size="small" type="number" label={t("calendar.settings.unitPrice")}
          value={inv.unit_price_huf || ""}
          onChange={(e) => setI("unit_price_huf", Number(e.target.value))}
          inputProps={{ min: 0, step: 500 }} />
        <FormControl size="small" fullWidth>
          <InputLabel id="cal-vat-label">{t("calendar.settings.vatKey")}</InputLabel>
          <Select labelId="cal-vat-label" label={t("calendar.settings.vatKey")}
            value={inv.vat_key} onChange={(e) => setI("vat_key", e.target.value)}>
            {VAT_KEYS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel id="cal-pay-label">{t("calendar.settings.paymentMethod")}</InputLabel>
          <Select labelId="cal-pay-label" label={t("calendar.settings.paymentMethod")}
            value={inv.payment_method} onChange={(e) => setI("payment_method", e.target.value)}>
            {PAYMENT_METHODS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" type="number" label={t("calendar.settings.dueDays")}
          value={inv.due_days} onChange={(e) => setI("due_days", Number(e.target.value))}
          inputProps={{ min: 0, max: 90 }} />
        <FormControlLabel
          control={<Checkbox size="small" checked={inv.e_invoice}
            onChange={(e) => setI("e_invoice", e.target.checked)} />}
          label={t("calendar.settings.eInvoice")}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
          {t("common.close")}
        </button>
        <button type="button" className="btn btn--primary btn--sm" onClick={save}>
          {t("common.save")}
        </button>
      </DialogActions>
    </Dialog>
  );
}
